import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, ChevronLeft, PackageCheck, Ruler, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import ProductCard from "@/components/ProductCard";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { colourHex } from "@/lib/colours";
import { T } from "@/constants/testIds";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState(null);
  const [colour, setColour] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setSize(null);
    setColour(null);
    setImgIndex(0);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        setSize((r.data.sizes || [])[1] || (r.data.sizes || ["M"])[0]);
        setColour((r.data.colours || [])[0] || "Black");
        return api.get("/products");
      })
      .then((r) => setRelated((r.data || []).filter((p) => p.id !== id).slice(0, 3)))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [id]);

  if (notFound)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="font-display text-3xl font-black uppercase text-stone-950">Piece not found</p>
        <Link to="/shop" className="bg-[#E52E39] px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">
          Back to the shop
        </Link>
      </div>
    );

  if (!product)
    return <div className="min-h-screen bg-[#FAF8F4]" />;

  const images = (product.images || []).filter(Boolean);
  const sizes = product.sizes || [];
  const colourChoices = (product.colours || []).length > 0 ? product.colours : ["Black", "White", "Grey", "Navy"];
  const chooseAnd = (fn) => (e) => {
    e.stopPropagation();
    if (!size) {
      toast.error("Choose a size first");
      return;
    }
    fn();
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-8 lg:px-16">
        <button
          onClick={() => navigate("/shop")}
          className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-stone-500 transition-colors hover:text-[#E52E39]"
        >
          <ChevronLeft className="h-4 w-4" /> Back to shop
        </button>

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          {/* Images */}
          <div>
            <div className="relative aspect-[4/5] overflow-hidden border border-stone-200 bg-[#FFFFFF]">
              {images.length > 0 ? (
                <img src={imgUrl(images[imgIndex])} alt={product.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center font-display text-6xl text-stone-300">V.I.A</div>
              )}
              {product.tag && (
                <span className="absolute left-4 top-4 bg-[#E52E39] px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                  {product.tag}
                </span>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-3">
                {images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIndex(i)}
                    className={`h-20 w-16 overflow-hidden border ${i === imgIndex ? "border-[#E52E39]" : "border-stone-200"}`}
                  >
                    <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <h1 className="font-display text-4xl font-black uppercase leading-none tracking-tight text-stone-950 sm:text-5xl">
              {product.name}
            </h1>
            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-mono text-3xl font-bold text-[#C79500]">{gbp(product.price)}</span>
              {product.compare_at_price > product.price && (
                <span className="font-mono text-lg text-stone-400 line-through">{gbp(product.compare_at_price)}</span>
              )}
            </div>

            <div className="mt-5 flex items-center gap-2 border border-stone-200 bg-[#FFFFFF] px-4 py-3">
              <Truck className="h-4 w-4 shrink-0 text-[#C79500]" />
              <span className="font-mono text-xs uppercase tracking-widest text-stone-700">
                Delivery: {product.delivery_time}
              </span>
            </div>

            <div className="mt-8">
              <div className="mb-3 flex items-center justify-between">
                <p className="via-label mb-0">Size</p>
                <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-stone-400">
                  <Ruler className="h-3 w-3" /> True to size
                </span>
              </div>
              <div data-testid={T.productSizeSelect(product.id)} className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`min-w-[56px] border px-4 py-3 font-display text-lg font-bold uppercase transition-colors ${
                      size === s
                        ? "border-[#E52E39] bg-[#E52E39] text-white"
                        : "border-stone-300 text-stone-700 hover:border-stone-600 hover:text-stone-950"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {colourChoices.length > 0 && (
              <div className="mt-6">
                <p className="via-label mb-3">
                  Colour <span className="ml-2 font-sans text-xs normal-case tracking-normal text-stone-500">{colour}</span>
                </p>
                <div data-testid={T.productColourSelect(product.id)} className="flex flex-wrap gap-3">
                  {colourChoices.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColour(c)}
                      title={c}
                      aria-label={`Colour ${c}`}
                      className={`flex h-11 w-11 items-center justify-center border-2 transition-all ${
                        colour === c ? "border-[#E52E39] shadow-[0_0_0_3px_rgba(229,46,57,0.2)]" : "border-stone-200 hover:border-stone-400"
                      }`}
                      style={{ backgroundColor: colourHex(c) }}
                    >
                      {colour === c && <Check className="h-4 w-4 mix-blend-difference" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid={T.productAddToCart(product.id)}
                onClick={chooseAnd(() => {
                  add(product, size, colour || "");
                  toast.success(`${product.name} (${size}${colour ? ", " + colour : ""}) added to bag`);
                })}
                className="flex-1 border border-stone-400 py-4 font-display text-lg font-black uppercase tracking-wide text-stone-950 transition-colors hover:border-[#E52E39] hover:text-[#E52E39]"
              >
                Add to bag
              </button>
              <button
                data-testid={T.productBuyNow(product.id)}
                onClick={chooseAnd(() => {
                  add(product, size, colour || "");
                  navigate("/checkout");
                })}
                className="flex-1 bg-[#E52E39] py-4 font-display text-lg font-black uppercase tracking-wide text-white transition-all hover:bg-[#c9202a] hover:shadow-[0_0_30px_rgba(229,46,57,0.35)]"
              >
                Buy now — card
              </button>
            </div>

            <p className="mt-8 text-base leading-relaxed text-stone-700">{product.description}</p>

            <div className="mt-8 border-t border-stone-200">
              {[
                ["Category", product.category],
                ["Delivery", product.delivery_time],
                ["Fulfilment", "Checked & dispatched by us personally"],
                ["Payment", "Secure card checkout via Stripe"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 border-b border-stone-200 py-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">{k}</span>
                  <span className="text-right text-sm text-stone-700">{v}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-start gap-2 text-stone-400">
              <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#C79500]" />
              <p className="text-xs leading-relaxed">
                Sourced to order — your piece is checked and dispatched when you buy. That keeps waste down and
                prices honest.
              </p>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-8 lg:px-16">
          <Reveal>
            <h2 className="mb-8 font-display text-2xl font-extrabold uppercase tracking-tight text-stone-950">
              More from V.I.A
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} className="h-full" />
              ))}
            </div>
          </Reveal>
        </div>
      )}
      <Footer />
    </div>
  );
}
